import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation, i18n } from 'next-i18next';
import { useAuth } from '@lb-classroom/feature-auth';
import LessonIssueApiUtils from '@utils/api/lessonIssue';
import MomentFormatter from '@utils/MomentFormatter';
import appUtils from '@utils/appUtils';
import type {
  ReportedIssueModalContainerProps,
  LessonIssue,
} from './reported-issue-modal.types';

export const useReportedIssueModalService = (
  props: ReportedIssueModalContainerProps,
) => {
  const { hideModal, lesson } = props;
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();

  const [issue, setIssue] = useState<LessonIssue | null>(
    lesson.lesson_issue || null,
  );
  const [isLoading, setIsLoading] = useState(!lesson.lesson_issue);

  useEffect(() => {
    const fetchIssue = async () => {
      if (!issue) {
        try {
          const result = await LessonIssueApiUtils.fetchOne(lesson.id);
          if (result?.lesson_issue) {
            setIssue(result.lesson_issue);
          }
        } catch {
          // Error fetching issue, leave as loading
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchIssue();
  }, [issue, lesson.id]);

  const formatter = useMemo(() => {
    const currentLocale = appUtils.getLocaleFromLang(i18n?.language || 'en');
    const timeZone = currentUser?.js_time_zone || 'UTC';
    return new MomentFormatter(currentLocale, timeZone);
  }, [currentUser?.js_time_zone]);

  const formattedData = useMemo(() => {
    const startFormat = formatter.lessonFormat(lesson.start_at);
    const endFormat = formatter.lessonFormat(lesson.end_at);

    let issueType = t('general.loading');
    let reportedAt = t('general.loading');

    if (issue) {
      issueType = issue.issue_types[0];
      reportedAt = formatter.getDateTimeFormat(issue.sent_at);
    }

    const consumedTickets =
      lesson.consumed_tickets !== null
        ? +lesson.consumed_tickets
        : t('modals.issue.reported.noData');
    const consumedNoExpirationTickets =
      lesson.consumed_sub_tickets !== null
        ? +lesson.consumed_sub_tickets
        : t('modals.issue.reported.noData');

    return {
      tutorName: lesson.tutor_name,
      lessonTime: `${startFormat.formattedTime} - ${endFormat.formattedTime}`,
      lessonDate: startFormat.formattedDate,
      consumedTickets,
      consumedNoExpirationTickets,
      issueType,
      reportedAt,
    };
  }, [formatter, lesson, issue, t]);

  const handleClose = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    ...formattedData,
    isLoading,
    onClose: handleClose,
  };
};
